/**
 * Catalog APIs for chatbot Create-PO review.
 * Product search uses Node `new_purchase_orders_product_list_v2` (same as New PO).
 */
import axios from 'axios';
import { getSessionAuthHeader, withSessionFields } from '../../Common/sessionAuth';
import {
  BASE_NODE_URL,
  BASE_URL,
  CATEGORY_LIST,
  NEW_PURCHASE_ORDER_PRODUCT_BY_UPC,
  NEW_PURCHASE_ORDER_PRODUCT_LIST,
  PURCHASE_ORDER_CREATE_PRODUCT,
} from '../../Constants/Config';
import {
  buildNewPurchaseOrderProductByUpcPayload,
  buildNewPurchaseOrderProductListPayload,
  fetchNewPurchaseOrderProductByUpc,
  fetchNewPurchaseOrderProductList,
  normalizeProductListProduct,
} from '../../services/purchaseOrders/purchaseOrdersApi';
import { toApiError } from '../../services/http/postNodeMultipart';
import { formatCreatePoCostPerItem, readCreatePoCostPerItemNumber } from './poCreateLogic';
import { ciUrl } from "../../Constants/nodeCi";

const mapPickerRow = (row) => {
  const productId = String(row?.product_id || '');
  if (!productId) return null;
  const variantRaw = row?.varient_id ?? row?.variant_id;
  const variantId =
    variantRaw == null || String(variantRaw).trim() === ''
      ? '0'
      : String(variantRaw);
  const title =
    [row?.product_name, row?.variant_name].filter(Boolean).join(' / ') ||
    String(row?.product_title || '').trim();
  const costNum = readCreatePoCostPerItemNumber(row);
  return {
    productId,
    variantId,
    title,
    upc: String(row?.upc || ''),
    stock: Number(row?.quantity) || 0,
    cost: costNum != null ? formatCreatePoCostPerItem(costNum) : '',
    raw: row,
  };
};

export const flattenV2ProductOptions = (products) =>
  (products || [])
    .flatMap((product) => {
      const group = product?.rowItems
        ? product
        : normalizeProductListProduct(product);
      return (group.rowItems || []).map(mapPickerRow);
    })
    .filter(Boolean);

export const mapUpcHitToOption = (data) => {
  if (!data || typeof data !== 'object') return null;
  return mapPickerRow({
    product_id: data.product_id,
    varient_id: data.varient_id ?? data.variant_id,
    product_name: data.product_name || data.product_title,
    variant_name: data.variant_name,
    upc: data.upc,
    quantity: data.quantity,
    cost_price: data.cost_price ?? data.cost_per_item,
    cost_per_item: data.cost_per_item,
    cost: data.cost,
  });
};

/** Catalog search / browse for Create PO line Item Name (empty query = first page). */
export async function searchChatbotPoProducts(auth, { merchantId, vendorId, query }) {
  const trimmed = String(query || '').trim();
  if (!merchantId) return [];
  // Typed searches need at least 2 chars; empty query browses the first page.
  if (trimmed.length === 1) return [];

  const result = await fetchNewPurchaseOrderProductList(
    buildNewPurchaseOrderProductListPayload(auth, {
      merchantId,
      vendorId: vendorId || '',
      categoryId: 'all',
      search: trimmed,
      page: 1,
      perPage: 50,
    }),
    undefined,
    { forceNodeApi: true },
  );
  return flattenV2ProductOptions(result?.products);
}

export async function lookupChatbotPoProductByUpc(auth, { merchantId, upc }) {
  const hit = await fetchNewPurchaseOrderProductByUpc(
    buildNewPurchaseOrderProductByUpcPayload(auth, {
      merchantId,
      upc: String(upc || '').replace(/\s/g, ''),
    }),
    undefined,
    { forceNodeApi: true },
  );
  return mapUpcHitToOption(hit);
}

/** PHP get_productdata_ById — same enrichment CreatePurchaseOrderPanel uses. */
export async function fetchChatbotPoProductById(auth, { merchantId, productId }) {
  try {
    const formData = new FormData();
    formData.append('merchant_id', String(merchantId || ''));
    formData.append('id', String(productId || ''));
    formData.append('token_id', String(auth?.token_id || ''));
    formData.append('login_type', String(auth?.login_type || ''));
    if (auth?.session_id) formData.append('session_id', String(auth.session_id));
    const response = await axios.post(
      ciUrl("Product_api_react/get_productdata_ById"),
      formData,
      {
        headers: {
          Authorization: `Bearer ${auth?.token || ''}`,
        },
      },
    );
    return response?.data || null;
  } catch (error) {
    throw toApiError(error);
  }
}

/** Resolve Quickadd category id (WebRegister / AI create-product convention). */
const quickAddCategoryCache = new Map(); // merchantId -> categoryId
const quickAddCategoryInflight = new Map(); // merchantId -> Promise

export async function fetchQuickAddCategoryId(merchantId) {
  const key = String(merchantId || '');
  if (!key) return '';
  if (quickAddCategoryCache.has(key)) {
    return quickAddCategoryCache.get(key);
  }
  if (quickAddCategoryInflight.has(key)) {
    return quickAddCategoryInflight.get(key);
  }

  const request = (async () => {
    try {
      const formData = new FormData();
      formData.append('merchant_id', key);
      formData.append('page', '1');
      formData.append('per_page', '100');
      const response = await axios.post(ciUrl(CATEGORY_LIST), withSessionFields(formData), {
        headers: getSessionAuthHeader(),
      });
      const rows = response?.data?.status ? response.data.result : [];
      const list = Array.isArray(rows) ? rows : [];
      const quick =
        list.find((row) =>
          String(row?.title || row?.name || '')
            .trim()
            .toLowerCase() === 'quickadd',
        ) ||
        list.find((row) =>
          String(row?.title || row?.name || '')
            .toLowerCase()
            .includes('quickadd'),
        );
      const categoryId = quick
        ? String(quick.id)
        : list[0]
          ? String(list[0].id)
          : '';
      quickAddCategoryCache.set(key, categoryId);
      return categoryId;
    } catch {
      return '';
    } finally {
      quickAddCategoryInflight.delete(key);
    }
  })();

  quickAddCategoryInflight.set(key, request);
  return request;
}

/**
 * Create catalog product via Quickadd_product_react/quick_add_product_new
 * (same upstream as AI `/chat/products/create`).
 */
export async function createChatbotPoProduct(auth, {
  merchantId,
  name,
  upc,
  sellingPrice,
  costPrice,
  quantity = 0,
  categoryId,
  categoryName = '',
  taxId = '',
}) {
  const selling = Number(sellingPrice) || 0;
  const cost = costPrice === '' || costPrice == null ? 0 : Number(costPrice) || 0;
  const profit = Math.max(selling - cost, 0);
  const margin =
    selling <= 0 ? 0 : cost <= 0 ? 100 : (profit / selling) * 100;

  const money = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(2) : '0.00';
  };

  try {
    const formData = new FormData();
    formData.append('merchant_id', String(merchantId || ''));
    formData.append('title', String(name || '').trim());
    formData.append('upc', String(upc || '').trim());
    formData.append('price', money(selling));
    formData.append('tax', String(taxId || '').split(',')[0].trim());
    formData.append('cat_id', String(categoryId || '').split(',')[0].trim());
    formData.append('category_name', String(categoryName || '').trim());
    formData.append('cost_per_item', money(cost));
    formData.append(
      'quantity',
      String(Math.max(0, Math.trunc(Number(quantity) || 0))),
    );
    formData.append('margin', margin.toFixed(2));
    formData.append('profit', profit.toFixed(2));
    formData.append('token_id', String(auth?.token_id || ''));
    formData.append('login_type', String(auth?.login_type || ''));
    if (auth?.session_id) formData.append('session_id', String(auth.session_id));

    const response = await axios.post(
      BASE_URL + PURCHASE_ORDER_CREATE_PRODUCT,
      formData,
      {
        headers: {
          Authorization: `Bearer ${auth?.token || ''}`,
        },
      },
    );
    return response?.data || {};
  } catch (error) {
    throw toApiError(error);
  }
}

/** Prefer Node UPC endpoint path for debugging / parity checks. */
export const CHATBOT_PO_PRODUCT_LIST_PATH =
  BASE_NODE_URL + NEW_PURCHASE_ORDER_PRODUCT_LIST;
export const CHATBOT_PO_PRODUCT_BY_UPC_PATH =
  BASE_NODE_URL + NEW_PURCHASE_ORDER_PRODUCT_BY_UPC;
